import { isKeeperEligibilityPlayer } from "./keeper-eligibility-player";

export const DRAFT_LAB_LEAGUE_SETUP_API_PREFIX = "/teambeer/api/draft-lab-setup";
export const DRAFT_LAB_LEAGUE_SETUP_SCHEMA = "team-beer-draft-lab-config";
export const DRAFT_LAB_LEAGUE_SETUP_SCHEMA_VERSION = 1;
export const DRAFT_LAB_LEAGUE_SETUP_CAPABILITY_HEADER =
  "X-Team-Beer-League-Setup-Capability";
export const DRAFT_LAB_LEAGUE_SETUP_CAPABILITY_TTL_SECONDS = 10 * 60;
export const DRAFT_LAB_LEAGUE_SETUP_PASSWORD_MIN_LENGTH = 10;
export const DRAFT_LAB_LEAGUE_SETUP_PASSWORD_MAX_LENGTH = 128;

export const DRAFT_LAB_CANONICAL_TEAM_NAMES = [
  "Beatdown Express",
  "Achilles Hell",
  "Son Of Vegas",
  "SnowBirds",
  "The Bryce is Right",
  "TuaFinity and Beyond",
  "LA Giants",
  "Cougar Hunters",
  "I'm Inevitable (Velour Fog)",
  "Bubblegum & DuckTape",
  "Allan's Team",
  "New Team Same Task",
] as const;

export type DraftLabCanonicalTeamName = typeof DRAFT_LAB_CANONICAL_TEAM_NAMES[number];

/**
 * These are the immutable eligibility lists embedded in Draft Lab. The global
 * configuration stores only additions and removals from this baseline. Keeping
 * the baseline next to the validator prevents mutable League Hub state from
 * silently changing the meaning of a saved Draft Lab delta.
 */
export const DRAFT_LAB_DEFAULT_ELIGIBLE_IDS: Readonly<
  Record<DraftLabCanonicalTeamName, readonly string[]>
> = Object.freeze({
  "Beatdown Express": Object.freeze([
    "cj stroud", "zay flowers", "dandre swift", "kyle pitts", "david montgomery",
    "jameson williams", "ceedee lamb", "garrett wilson", "terry mclaurin",
    "dillon gabriel", "omarion hampton",
  ]),
  "Achilles Hell": Object.freeze([
    "jj mccarthy", "dk metcalf", "bucky irving", "tj hockenson", "jerry jeudy",
    "bo nix", "daniel jones", "hollywood brown", "jayden reed", "brandon aiyuk",
    "tyler loop", "bhayshul tuten", "jaylin noel", "dylan sampson",
  ]),
  "Son Of Vegas": Object.freeze([
    "jamarr chase", "bijan robinson", "james cook", "chase brown", "brandon aubrey",
    "aj brown", "patrick mahomes", "brian robinson", "jayden daniels",
    "michael penix", "colston loveland", "luther burden",
  ]),
  SnowBirds: Object.freeze([
    "sam darnold", "travis etienne", "devonta smith", "michael pittman",
    "rachaad white", "jk dobbins", "tyler warren",
  ]),
  "The Bryce is Right": Object.freeze([
    "dak prescott", "dj moore", "kyren williams", "christian mccaffrey",
    "chuba hubbard", "bryce young", "lamar jackson", "xavier legette",
    "christian kirk", "jalen coker", "braelon allen", "jayden higgins",
    "rj harvey", "kaleb johnson", "elijah arroyo",
  ]),
  "TuaFinity and Beyond": Object.freeze([
    "jaylen waddle", "chris olave", "christian watson", "marvin harrison",
    "tyrone tracy", "ricky pearsall", "ashton jeanty", "oronde gadsden",
    "tre harris", "pat bryant", "jalen milroe", "terrance ferguson",
  ]),
  "LA Giants": Object.freeze([
    "puka nacua", "jaylen warren", "breece hall", "caleb williams", "romeo doubs",
    "tucker kraft", "isaac guerendo", "anthony richardson", "andy borregales",
    "jaxson dart",
  ]),
  "Cougar Hunters": Object.freeze([
    "justin herbert", "tee higgins", "devon achane", "kenneth walker",
    "dalton kincaid", "tua tagovailoa", "kyler murray", "xavier worthy",
    "tyreek hill", "joe mixon", "woody marks", "emeka egbuka", "isaac teslaa",
  ]),
  "I'm Inevitable (Velour Fog)": Object.freeze([
    "jalen hurts", "justin jefferson", "deebo samuel", "saquon barkley",
    "drake london", "rashee rice", "josh jacobs", "rome odunze", "sam laporta",
    "jonathon brooks", "cam ward", "mason taylor", "jack bech",
  ]),
  "Bubblegum & DuckTape": Object.freeze([
    "jaxon smith njigba", "jonathan taylor", "george pickens", "javonte williams",
    "jahmyr gibbs", "brock purdy", "alvin kamara", "justin fields",
    "tetairoa mcmillan", "matthew golden",
  ]),
  "Allan's Team": Object.freeze([
    "drake maye", "mike evans", "derrick henry", "ladd mcconkey",
    "rhamondre stevenson", "josh allen", "isiah pacheco", "kyle monangai",
    "treveyon henderson", "quinshon judkins", "shedeur sanders", "elic ayomanor",
    "ollie gordon", "will howard", "jawhar jordan",
  ]),
  "New Team Same Task": Object.freeze([
    "joe burrow", "brian thomas", "amon ra st brown", "trevor lawrence",
    "brock bowers", "trey benson", "malik nabers", "jacory croskey merritt",
    "harold fannin", "chimere dike", "tyler shough", "devin neal", "cam skattebo",
    "quinn ewers", "travis hunter",
  ]),
});

export interface DraftLabLeagueSetupConfig {
  version: 1;
  aliases: Partial<Record<DraftLabCanonicalTeamName, string>>;
  eligibleAdds: Partial<Record<DraftLabCanonicalTeamName, string[]>>;
  eligibleRemoves: Partial<Record<DraftLabCanonicalTeamName, string[]>>;
}

export interface DraftLabLeagueSetupConfigDocument {
  schema: typeof DRAFT_LAB_LEAGUE_SETUP_SCHEMA;
  schemaVersion: typeof DRAFT_LAB_LEAGUE_SETUP_SCHEMA_VERSION;
  revision: number;
  config: DraftLabLeagueSetupConfig;
}

export interface DraftLabConfigPlayer {
  id: string;
  positions: readonly string[];
}

export class DraftLabLeagueSetupConfigError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "DraftLabLeagueSetupConfigError";
  }
}

const TEAM_NAME_SET = new Set<string>(DRAFT_LAB_CANONICAL_TEAM_NAMES);
const CONFIG_KEYS = ["aliases", "eligibleAdds", "eligibleRemoves", "version"] as const;
const MAX_REFERENCED_PLAYER_IDS = 400;
const UNSAFE_TEXT = /[\p{Cc}\p{Cf}\p{Cs}]/u;

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hasExactKeys(value: Record<string, unknown>, keys: readonly string[]): boolean {
  const actual = Object.keys(value).sort();
  const expected = [...keys].sort();
  return actual.length === expected.length
    && actual.every((key, index) => key === expected[index]);
}

function invalid(message: string): never {
  throw new DraftLabLeagueSetupConfigError(message);
}

function normalizeAlias(value: unknown, canonicalName: string): string {
  if (typeof value !== "string" || UNSAFE_TEXT.test(value)) {
    invalid(`The alias for ${canonicalName} is invalid.`);
  }
  const normalized = value.normalize("NFKC").trim().replace(/\s+/gu, " ");
  if (!normalized || normalized.length > 80 || normalized !== value || normalized === canonicalName) {
    invalid(`The alias for ${canonicalName} is not a normalized non-default name.`);
  }
  return normalized;
}

function playerIdArray(value: unknown, teamName: string, field: string): string[] {
  if (!Array.isArray(value)) invalid(`${field}.${teamName} must be an array.`);
  if (value.length > MAX_REFERENCED_PLAYER_IDS) {
    invalid(`${field}.${teamName} contains too many players.`);
  }
  const output = value.map((item) => {
    if (
      typeof item !== "string"
      || item.length < 1
      || item.length > 160
      || item.trim() !== item
      || UNSAFE_TEXT.test(item)
    ) {
      invalid(`${field}.${teamName} contains an invalid player id.`);
    }
    return item;
  });
  if (new Set(output).size !== output.length) {
    invalid(`${field}.${teamName} contains a duplicate player id.`);
  }
  return output;
}

export function emptyDraftLabLeagueSetupConfig(): DraftLabLeagueSetupConfig {
  return { version: 1, aliases: {}, eligibleAdds: {}, eligibleRemoves: {} };
}

export function draftLabLeagueSetupEtag(revision: number): string {
  if (!Number.isSafeInteger(revision) || revision < 0) {
    throw new Error("Draft Lab League Setup revision is invalid.");
  }
  return `"draft-lab-setup-v1-r${revision}"`;
}

export function draftLabLeagueSetupConfigDocument(
  config: DraftLabLeagueSetupConfig,
  revision: number,
): DraftLabLeagueSetupConfigDocument {
  return {
    schema: DRAFT_LAB_LEAGUE_SETUP_SCHEMA,
    schemaVersion: DRAFT_LAB_LEAGUE_SETUP_SCHEMA_VERSION,
    revision,
    config,
  };
}

export function referencedDraftLabConfigPlayerIds(input: unknown): string[] {
  if (!isRecord(input) || !isRecord(input.eligibleAdds) || !isRecord(input.eligibleRemoves)) {
    return [];
  }
  const ids = new Set<string>();
  for (const map of [input.eligibleAdds, input.eligibleRemoves]) {
    for (const value of Object.values(map)) {
      if (!Array.isArray(value)) continue;
      for (const item of value) if (typeof item === "string") ids.add(item);
    }
  }
  return [...ids];
}

/**
 * Validates and canonicalizes the complete global Draft Lab delta. Player
 * existence comes from D1, while baseline ownership remains immutable here.
 */
export function normalizeDraftLabLeagueSetupConfig(
  input: unknown,
  playersById: ReadonlyMap<string, DraftLabConfigPlayer>,
): DraftLabLeagueSetupConfig {
  if (!isRecord(input) || !hasExactKeys(input, CONFIG_KEYS) || input.version !== 1) {
    invalid("The Draft Lab configuration envelope is invalid.");
  }
  const aliasesInput = input.aliases;
  const addsInput = input.eligibleAdds;
  const removesInput = input.eligibleRemoves;
  if (!isRecord(aliasesInput) || !isRecord(addsInput) || !isRecord(removesInput)) {
    invalid("The Draft Lab configuration maps are invalid.");
  }
  for (const map of [aliasesInput, addsInput, removesInput]) {
    if (Object.keys(map).some((teamName) => !TEAM_NAME_SET.has(teamName))) {
      invalid("The Draft Lab configuration contains an unknown team.");
    }
  }

  const aliases: DraftLabLeagueSetupConfig["aliases"] = {};
  const eligibleAdds: DraftLabLeagueSetupConfig["eligibleAdds"] = {};
  const eligibleRemoves: DraftLabLeagueSetupConfig["eligibleRemoves"] = {};
  const displayNames: string[] = [];
  let referencedCount = 0;

  for (const teamName of DRAFT_LAB_CANONICAL_TEAM_NAMES) {
    const aliasValue = aliasesInput[teamName];
    if (aliasValue !== undefined) aliases[teamName] = normalizeAlias(aliasValue, teamName);
    displayNames.push(aliases[teamName] ?? teamName);

    const adds = addsInput[teamName] === undefined
      ? []
      : playerIdArray(addsInput[teamName], teamName, "eligibleAdds");
    const removes = removesInput[teamName] === undefined
      ? []
      : playerIdArray(removesInput[teamName], teamName, "eligibleRemoves");
    referencedCount += adds.length + removes.length;
    if (adds.some((playerId) => removes.includes(playerId))) {
      invalid(`${teamName} cannot add and remove the same player.`);
    }

    const defaults = new Set(DRAFT_LAB_DEFAULT_ELIGIBLE_IDS[teamName]);
    if (adds.some((playerId) => defaults.has(playerId))) {
      invalid(`${teamName} contains an addition that is already eligible by default.`);
    }
    if (removes.some((playerId) => !defaults.has(playerId))) {
      invalid(`${teamName} contains a removal that is not eligible by default.`);
    }
    for (const playerId of [...adds, ...removes]) {
      const player = playersById.get(playerId);
      if (!player || player.id !== playerId || !isKeeperEligibilityPlayer(player)) {
        invalid(`${teamName} references an unknown or ineligible player.`);
      }
    }
    if (adds.length) eligibleAdds[teamName] = adds;
    if (removes.length) eligibleRemoves[teamName] = removes;
  }

  if (referencedCount > MAX_REFERENCED_PLAYER_IDS) {
    invalid("The Draft Lab configuration contains too many player references.");
  }
  const foldedNames = displayNames.map((name) =>
    name.normalize("NFKC").toLocaleLowerCase("en-US"));
  if (new Set(foldedNames).size !== foldedNames.length) {
    invalid("Draft Lab team display names must be unique.");
  }

  const ownerByPlayer = new Map<string, string>();
  for (const teamName of DRAFT_LAB_CANONICAL_TEAM_NAMES) {
    const removed = new Set(eligibleRemoves[teamName] ?? []);
    const effectiveIds = [
      ...DRAFT_LAB_DEFAULT_ELIGIBLE_IDS[teamName].filter((id) => !removed.has(id)),
      ...(eligibleAdds[teamName] ?? []),
    ];
    for (const playerId of effectiveIds) {
      const existingOwner = ownerByPlayer.get(playerId);
      if (existingOwner) {
        invalid(`${playerId} would be eligible for both ${existingOwner} and ${teamName}.`);
      }
      ownerByPlayer.set(playerId, teamName);
    }
  }

  return { version: 1, aliases, eligibleAdds, eligibleRemoves };
}

